import { Component, ChangeDetectorRef, Inject } from '@angular/core';
import { SharedModule } from 'src/app/shared/shared.module';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { DropdownComponent } from 'src/app/shared/components/dropdown/dropdown.component';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { AssetManagementService } from 'src/app/pages/asset-management/asset-management-service';
import { OffboardingRequestsTableService } from '@app/pages/asset-management/asset-management-table/offboarding-requests-table.service';
import { ToasterService } from '@app/shared/services/toaster.service';

import {
  StatusLabelComponent,
  StatusType,
} from 'src/app/shared/components/Status-Label/status-label.component';
import { REASON_OPTIONS, RELEASE_TYPE_OPTIONS } from '@app/pages/asset-management/asset-management.config';
import { ASSET_MANAGEMENT_CONSTANTS } from '@app/shared/constants/constants';
@Component({
  selector: 'app-offboarding-request',
  standalone: true,
  imports: [
    SharedModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatFormFieldModule,
    MatInputModule,
    StatusLabelComponent,
    DropdownComponent
  ],
  templateUrl: './offboarding-request.component.html',
  styleUrl: './offboarding-request.component.scss',
})
export class OffboardingRequestComponent {
  comments: string = '';
  dateOfOffboarding: string = '';
  dateOfReturn: string = '';
  psId: string = '';
  releaseType: string = ''; 
  reason: string = '';
  releaseTypeOptions = RELEASE_TYPE_OPTIONS;
  reasonOptions = REASON_OPTIONS;
  constructor(
    private dialogRef: MatDialogRef<OffboardingRequestComponent>,
    private assetManagementService: AssetManagementService,
    private offboardingTableService: OffboardingRequestsTableService,
    private cdr: ChangeDetectorRef,
     @Inject(MAT_DIALOG_DATA) public data: any,
  ) {
    if (this.data?.psId) {
      this.psId = this.data.psId;
    }
  }

  selectedStatus: StatusType = 'Initiated';

   get isPsIdDisabled(): boolean {
    return this.data?.disablePsId === true;
  }

  get isSaveDisabled(): boolean {
    const hasPsId = !!this.psId?.trim();
    const hasOffboarding = !!this.dateOfOffboarding;
    const hasReleaseType = !!this.releaseType;
    const hasComments = !!this.comments?.trim();
    const isDone = this.selectedStatus?.toLowerCase() === 'done';
    const hasReturnIfDone = isDone ? !!this.dateOfReturn : true;
    const hasReasonIfInvoluntary = this.releaseType === ASSET_MANAGEMENT_CONSTANTS.inVoluntary ? !!this.reason : true;
    return !(hasPsId && hasOffboarding && hasReleaseType && hasComments && hasReturnIfDone && hasReasonIfInvoluntary);
  }

  handleCancel(): void {
    this.dialogRef.close();
  }

  closeForm(): void {
    this.handleCancel();
  }

  handleSubmit(): void {
    if (!this.psId?.trim()) {
      (this.assetManagementService as any).toasterService?.error(
        'PS ID is mandatory.',
        'Validation Error',
      );
      return;
    }
    if (!this.dateOfOffboarding) {
      (this.assetManagementService as any).toasterService?.error(
        'Date of Off-boarding is mandatory.',
        'Validation Error',
      );
      return;
    }
    if (!this.releaseType) {
      (this.assetManagementService as any).toasterService?.error(
        'Voluntary/Involuntary Release selection is mandatory.',
        'Validation Error',
      );
      return;
    }

    if (this.releaseType === ASSET_MANAGEMENT_CONSTANTS.inVoluntary && !this.reason) {
      (this.assetManagementService as any).toasterService?.error(
        'Reason is required when Involuntary is selected.',
        'Validation Error',
      );
      return;
    }

    if (!this.comments?.trim()) {
      (this.assetManagementService as any).toasterService?.error(
        'Comments field is mandatory.',
        'Validation Error',
      );
      return;
    }

    if (!this.releaseType) {
      (this.assetManagementService as any).toasterService?.error(
        'Voluntary/Involuntary Release selection is mandatory.',
        'Validation Error',
      );
      return;
    }

    if (this.releaseType === ASSET_MANAGEMENT_CONSTANTS.inVoluntary && !this.reason) {
      (this.assetManagementService as any).toasterService?.error(
        'Reason is required when Involuntary is selected.',
        'Validation Error',
      );
      return;
    }

    if (!this.comments?.trim()) {
      (this.assetManagementService as any).toasterService?.error(
        'Comments field is mandatory.',
        'Validation Error',
      );
      return;
    }

    if (this.selectedStatus?.toLowerCase() === 'done' && !this.dateOfReturn) {
      (this.assetManagementService as any).toasterService?.error(
        'Date of Return is required when status is Done.',
      );
      return;
    }

    if (this.dateOfReturn && this.dateOfOffboarding) {
      const returnDate = new Date(this.dateOfReturn);
      const offboardingDate = new Date(this.dateOfOffboarding);
      if (returnDate < offboardingDate) {
        (this.assetManagementService as any).toasterService?.error(
          'Return date should be after Offboarding date.',
          'Validation Error',
        );
        return;
      }
    }

    const statusValue = this.selectedStatus;
    const formatDate = (dateStr: string) => {
      if (!dateStr) return '';
      const d = new Date(dateStr);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    const request = {
      employeeId: this.psId,
      dateOfReturn: formatDate(this.dateOfReturn),
      dateOfOffboarding: formatDate(this.dateOfOffboarding),
      releaseType: this.releaseType,
      reason: this.releaseType === ASSET_MANAGEMENT_CONSTANTS.inVoluntary ? this.reason : '',
      status: statusValue,
      comments: this.comments,
      commentsLabel: 'Offboarding',
    };
    this.assetManagementService.createOffboardingRequest(request).subscribe({
      next: (res) => {
        (this.assetManagementService as any).toasterService?.success(
          'Offboarding request saved successfully.',
        );
        this.offboardingTableService.refreshData();
        this.dialogRef.close(true);
      },
      error: (err) => {
        const errorMsg = err?.error || err?.message;
        (this.assetManagementService as any).toasterService?.error(
          errorMsg,
          'Error',
        );
      },
    });
  }

  onStatusChanged(status: any) {
    if (typeof status === 'string') {
      this.selectedStatus = status as StatusType;
    }
  }

    onReleaseTypeChange(selectedItem: any) {
    this.releaseType = selectedItem?.value || selectedItem;
    if (this.releaseType !== ASSET_MANAGEMENT_CONSTANTS.inVoluntary) {
      this.reason = '';
    }
    this.cdr.detectChanges();
  }

  onReasonChange(selectedItem: any) {
    this.reason = selectedItem?.value || selectedItem;
  }
}
